import { useEffect, useState, useMemo } from "react";
import {
  Package,
  Save,
  // ArrowLeft,
  Plane,
  Building2,
  MapPin,
  BedDouble,
  ChevronDown,
  Settings2,
  Trash2,
  Plus,
} from "lucide-react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { Label } from "../../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "ui/tabs";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Checkbox } from "../../../ui/checkbox";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  CreateLawazimPayload,
  // CancellationPolicySlab,
  CancellationPolicyConfig,
  CancellationTier,
  AtraafType,
  FormErrors,
  LawazimConfig,
  PortType,
  PropertyOption,
  PropertyType,
  RoomTypeCharges,
  InterVenueChargeRow,
} from "../../../types/lawazimConfig";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "ui/accordion";
import { FullScreenLoading } from "components/ui/loader";
import {
  createLawazimConfig,
  getAtraafList,
  getPortList,
  getPropertyRoomTypes,
  getPropertyTypesList,
} from "services/lawazimConfigService";
import { getProperties } from "services/propertyDetailsService";
import { getVenuesOptions } from "services/venueService";
import {
  getActiveTourServices,
  // getTourServices,
  TourServiceApi,
} from "services/tourServiceMasterService";
import { useScreenPermission } from "hooks/useScreenPermission";
import { SelectTriggerUI } from "ui/selectTrigger";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "ui/select";
import axios from "axios";
import ConfirmDialog from "ui/ConfirmDialog";
import { usePageHeader } from 'contexts/PageHeaderContext';


type VenueOption = {
  venueId: number;
  publicId?: number;
  venueName: string;
};

type ExternalHotelChargeRow = {
  id: string;
  propertyPublicId: string;
  infantCharge: number;
  childCharge: number;
  adultCharge: number;
  seniorCharge: number;
  extraCharge: number;
};

const createExternalHotelChargeRow = (): ExternalHotelChargeRow => ({
  id: `external-hotel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  propertyPublicId: "",
  infantCharge: 0,
  childCharge: 0,
  adultCharge: 0,
  seniorCharge: 0,
  extraCharge: 0,
});

type DeleteConfirmTarget =
  | { type: "cancellation-tier"; id: string }
  | { type: "inter-venue-charge"; id: string };


export interface PropertyRoomType {
  // roomTypeId?:string,
  roomTypeId: string;
  roomTypeCode: string;
  propertyId: string;
  propertyRoomTypePublicId: string;
  roomTypePublicId: string;
  roomTypeName: string;
  basePrice: number;
  earlyCheckInPricePerHour: number;
  lateCheckOutPricePerHour: number;
  extraCharge: number;
}

export interface PropertyRoomTypeResponse {
  propertyPublicId: string;
  propertyName: string;
  roomTypes: PropertyRoomType[];
}

const createInterVenueChargeRow = (): InterVenueChargeRow => ({
  id: `inter-venue-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  fromVenueId: "",
  toVenueId: "",
  infantCharge: 0,
  childCharge: 0,
  adultCharge: 0,
  seniorCharge: 0,
  extraCharge: 0,
});

export function AddLawazimConfiguration() {
  const { setPageHeader } = usePageHeader();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { canAdd } = useScreenPermission();
  const [propertyTypeOption, setPropertyTypeOptions] = useState<PropertyType[]>([]);
  const [atraafTypeOption, setAtraafTypeOptions] = useState<AtraafType[]>([]);
  const [portOption, setPortOptions] = useState<PortType[]>([]);
  const [venueOptions, setVenueOptions] = useState<VenueOption[]>([]);
  const [chargeableServices, setChargeableServices] = useState<TourServiceApi[]>([]);
  const [activeConfigTab, setActiveConfigTab] = useState("room-type");
  const [miqaatAccommodationMode, setMiqaatAccommodationMode] = useState<"room-type" | "property-type">("property-type");
  const [propertyPickerOpen, setPropertyPickerOpen] = useState(false);
  const [interVenueCharges, setInterVenueCharges] = useState<InterVenueChargeRow[]>(() => [createInterVenueChargeRow()]);
  const [externalHotelCharges, setExternalHotelCharges] = useState<ExternalHotelChargeRow[]>(() => [createExternalHotelChargeRow()]);
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<DeleteConfirmTarget | null>(null);

  const fallbackRoomTypes: RoomTypeCharges[] = [
    {
      roomTypeId: "1",
      roomTypeCode: "SNG",
      roomTypeName: "Single Room",
      minOccupancy: 1,
      maxOccupancy: 1,
      basePrice: 0,
      earlyCheckInPricePerHour: 0,
      lateCheckOutPricePerHour: 0,
      extraCharge: 0,
    },
    {
      roomTypeId: "2",
      roomTypeCode: "DBL",
      roomTypeName: "Double Room",
      minOccupancy: 1,
      maxOccupancy: 2,
      basePrice: 0,
      earlyCheckInPricePerHour: 0,
      lateCheckOutPricePerHour: 0,
      extraCharge: 0,
    },
    {
      roomTypeId: "3",
      roomTypeCode: "TWN",
      roomTypeName: "Twin Room",
      minOccupancy: 2,
      maxOccupancy: 2,
      basePrice: 0,
      earlyCheckInPricePerHour: 0,
      lateCheckOutPricePerHour: 0,
      extraCharge: 0,
    },
    {
      roomTypeId: "4",
      roomTypeCode: "TPL",
      roomTypeName: "Triple Room",
      minOccupancy: 2,
      maxOccupancy: 3,
      basePrice: 0,
      earlyCheckInPricePerHour: 0,
      lateCheckOutPricePerHour: 0,
      extraCharge: 0,
    },
    {
      roomTypeId: "5",
      roomTypeCode: "QAD",
      roomTypeName: "Quad Room",
      minOccupancy: 3,
      maxOccupancy: 4,
      basePrice: 0,
      earlyCheckInPricePerHour: 0,
      lateCheckOutPricePerHour: 0,
      extraCharge: 0,
    },
    {
      roomTypeId: "6",
      roomTypeCode: "SUT",
      roomTypeName: "Suite",
      minOccupancy: 1,
      maxOccupancy: 2,
      basePrice: 0,
      earlyCheckInPricePerHour: 0,
      lateCheckOutPricePerHour: 0,
      extraCharge: 0,
    },
  ];

  const createCancellationTier = (
    daysBeforeCheckIn: number,
    penaltyPercentage: number,
    penaltyBasis: CancellationTier["penaltyBasis"] = "",
  ): CancellationTier => ({
    id: `tier-${daysBeforeCheckIn}-${penaltyPercentage}-${Math.random().toString(36).slice(2, 8)}`,
    daysBeforeCheckIn,
    penaltyPercentage,
    penaltyBasis,
  });

  const cancellationPolicyTemplates: Record<
    CancellationPolicyConfig["policyType"],
    { label: string; description: string; tiers: Array<{ days: number; penalty: number }> }
  > = {
    standard: {
      label: "Standard Policy",
      description:
        "60+ days = 20%, 45-59 days = 50%, 30-44 days = 75%, 0-29 days = 100%.",
      tiers: [
        { days: 60, penalty: 20 },
        { days: 45, penalty: 50 },
        { days: 30, penalty: 75 },
        { days: 0, penalty: 100 },
      ],
    },
    peak: {
      label: "Peak Season (Strict)",
      description:
        "90+ days = 40%, 60-89 days = 75%, 45-59 days = 100%, within 45 days = non-refundable.",
      tiers: [
        { days: 90, penalty: 40 },
        { days: 60, penalty: 75 },
        { days: 45, penalty: 100 },
        { days: 0, penalty: 100 },
      ],
    },
    nonrefund: {
      label: "Non-Refundable",
      description: "100% non-refundable from the moment of booking.",
      tiers: [{ days: 0, penalty: 100 }],
    },
    group: {
      label: "Group Booking (10+)",
      description:
        "120+ days = 20%, 90-119 days = 50%, 60-89 days = 75%, within 60 days = 100%.",
      tiers: [
        { days: 120, penalty: 20 },
        { days: 90, penalty: 50 },
        { days: 60, penalty: 75 },
        { days: 0, penalty: 100 },
      ],
    },
  };

  const defaultCancellationPolicyConfig: CancellationPolicyConfig = {
    linkedLawazimCode: "",
    linkedLawazimName: "",
    hotelId: "",
    hotelName: "",
    roomTypeId: "",
    roomTypeName: "",
    appliesToRoomTypeCharges: [],
    policyType: "standard",
    freeCancellationHours: 0,
    minimumFee: 50,
    noShowPenaltyPercentage: 100,
    tiers: cancellationPolicyTemplates.standard.tiers.map((tier) =>
      createCancellationTier(tier.days, tier.penalty),
    ),
  };


  const fallbackProperties: PropertyOption[] = [
    { publicId: "faiz-karbala", propertyName: "Faiz E Husayni Karbala" },
    { publicId: "najaf-residence", propertyName: "Al Najaf Residence" },
    { publicId: "qasr-al-dur", propertyName: "Qasr Al Dur" },
    { publicId: "baghdad-hotel", propertyName: "Baghdad Hotel" },
  ];

  const [propertyOptions, setPropertyOptions] = useState<PropertyOption[]>(fallbackProperties);
  const [propertyRoomTypes, setPropertyRoomTypes] = useState<PropertyRoomTypeResponse[]>([]);


  const [errors, setErrors] = useState<FormErrors>({
    code: "",
    lawazimName: "",
    dateFrom: "",
    dateTo: "",
    chargeType: "",
    maxDays: "",
    dailyRateType: "",
    property: "",
    roomTypeConfiguration: "",
    interVenueCharges: "",
  });


  // Sample data
  const [formData, setFormData] = useState<
    Omit<LawazimConfig, "id" | "createdAt" | "isActive">
  >({
    code: "",
    lawazimName: "",
    forMiqaat: false,
    chargeType: "daily-rate",
    dailyRateType: undefined,
    dateFrom: "",
    dateTo: "",
    maxDays: undefined,
    roomTypeCharges: fallbackRoomTypes,
    cancellationPolicyConfig: defaultCancellationPolicyConfig,
    propertyIds: [],
    additionalServiceCharges: [],
    propertyTypeCharges: propertyTypeOption.map((propType) => ({
      propertyTypeId: String(propType.id),
      propertyTypeName: propType.name,
      infantCharge: 0,
      childCharge: 0,
      adultCharge: 0,
      seniorCharge: 0,
      extraCharge: 0,
    })),
    atraafCharges: atraafTypeOption
      .filter((atraaf) => atraaf.lawazimRequired)
      .map((atraaf) => ({
        atraafId: atraaf.atraafId,
        atraafName: atraaf.atraafName,
        infantCharge: 15,
        childCharge: 25,
        adultCharge: 40,
        seniorCharge: 35,
        extraCharge: 0,
      })),
    arrivalCharges: portOption.map((port) => ({
      portId: String(port.portId),
      portName: port.portName,
      infantCharge: 0,
      childCharge: 0,
      adultCharge: 0,
      seniorCharge: 0,
      extraCharge: 0,
    })),
    departureCharges: portOption.map((port) => ({
      portId: String(port.portId),
      portName: port.portName,
      infantCharge: 0,
      childCharge: 0,
      adultCharge: 0,
      seniorCharge: 0,
      extraCharge: 0,
    })),
  });

  const fetchRoomTypesByProperties = async (propertyPublicIds: string[]) => {
    try {
      const response = await getPropertyRoomTypes(propertyPublicIds);
      const updatedResponse: PropertyRoomTypeResponse[] = response.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (property: any) => ({
          ...property,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          roomTypes: property.roomTypes.map((room: any) => ({
            ...room,
            basePrice: 0,
            earlyCheckInPricePerHour: 0,
            lateCheckOutPricePerHour: 0,
            extraCharge: 0,
          })),
        }),
      );

      setPropertyRoomTypes(updatedResponse);
      setFormData((previous) => ({
        ...previous,
        roomTypeCharges: updatedResponse.flatMap(
          ({ propertyPublicId, propertyName, roomTypes }) =>
            roomTypes.map((room) => {
              const existingCharge = previous.roomTypeCharges?.find(
                (charge) =>
                  charge.roomPublicId === room.propertyRoomTypePublicId,
              );

              return {
                roomTypeId: room.roomTypeId,
                roomPublicId: room.propertyRoomTypePublicId,
                roomTypeCode: room.roomTypeCode,
                roomTypeName: room.roomTypeName,
                propertyId: propertyPublicId,
                propertyName,
                basePrice: existingCharge?.basePrice ?? room.basePrice,
                earlyCheckInPricePerHour:
                  existingCharge?.earlyCheckInPricePerHour ??
                  room.earlyCheckInPricePerHour,
                lateCheckOutPricePerHour:
                  existingCharge?.lateCheckOutPricePerHour ??
                  room.lateCheckOutPricePerHour,
                extraCharge: existingCharge?.extraCharge ?? room.extraCharge,
              };
            }),
        ),
      }));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load room types");
    }
  };

  useEffect(() => {
    setPageHeader({
      title: 'Add Lawazim Configuration',
      icon: <Package className="h-6 w-6" />,
      onBack: () => navigate(-1),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, navigate]);


  // MASTER APIS CALLING START
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        setLoading(true);
        const [
          atraafRes,
          portRes,
          propertyRes,
          propertyListRes,
          venueRes,
          tourServiceRes,
        ] = await Promise.allSettled([
          getAtraafList(true),
          getPortList(true),
          getPropertyTypesList(true),

          // getTourServices({ isActive: true, pageNo: 1, pageSize: 500 }),

          //active ids
          getProperties(0, 500), // DROPDOWN PROPERTY OPTIONS
          getVenuesOptions(),
          getActiveTourServices(true)
        ]);

        //ATRAAF API RESPONSE
        if (atraafRes.status === "fulfilled") {
          setAtraafTypeOptions(atraafRes.value);
        }

        // PORT API RESPONSE
        if (portRes.status === "fulfilled") {
          setPortOptions(portRes.value);
        }

        // PROPERTY TYPE RESPONSE
        if (propertyRes.status === "fulfilled") {
          setPropertyTypeOptions(propertyRes?.value)
        }

        // VENUE API RESPOSNE
        if (venueRes.status === "fulfilled") {
          setVenueOptions(venueRes.value);
        }

        // ADDITIONAL CHARGES API RESPONSE
        if (tourServiceRes.status === "fulfilled") {
          setChargeableServices(tourServiceRes.value);
        }


        //DROPDOWN PROPERTY OPTIONS
        if (
          propertyListRes.status === "fulfilled" &&
          propertyListRes.value.items?.length
        ) {
          const activeProperties = propertyListRes.value.items.filter(
            (property) => property.isActive,
          );
          setPropertyOptions(
            activeProperties.map((property) => ({
              publicId: property.publicId,
              propertyName: property.propertyName,
            })),
          );

          const propertyPublicIds = activeProperties.map(
            (property) => property.publicId,
          );

          setFormData((prev) => ({
            ...prev,
            propertyIds: propertyPublicIds,
          }));

          // Call Room Types API
          await fetchRoomTypesByProperties(propertyPublicIds);
        }


      } catch {
        toast.error("Failed to load master data");
      } finally {
        setLoading(false);
      }
    };

    fetchMasterData();
  }, []);
  //MASTER API FUNCTION ENDS


  useEffect(() => {
    if (propertyOptions.length === 0) return;
    const allPropertyIds = propertyOptions.map((property) => property.publicId);
    setFormData((prev) => {
      const currentIds = prev.propertyIds || [];
      const alreadyAllSelected =
        currentIds.length === allPropertyIds.length &&
        allPropertyIds.every((id) => currentIds.includes(id));
      return alreadyAllSelected
        ? prev
        : { ...prev, propertyIds: allPropertyIds };
    });
  }, [propertyOptions]);


  useEffect(() => {
    if (
      propertyTypeOption.length > 0 &&
      formData.propertyTypeCharges.length === 0
    ) {
      setFormData((prev) => ({
        ...prev,
        propertyTypeCharges: propertyTypeOption.map((propType) => ({
          propertyTypeId: String(propType.id),
          propertyTypePublicId: String(propType.publicId),
          propertyTypeName: propType.name,
          infantCharge: 0,
          childCharge: 0,
          adultCharge: 0,
          seniorCharge: 0,
          extraCharge: 0,
        })),
      }));
    }
  }, [propertyTypeOption, formData.propertyTypeCharges]);

  useEffect(() => {
    if (atraafTypeOption.length > 0 && formData.atraafCharges.length === 0) {
      setFormData((prev) => ({
        ...prev,
        atraafCharges: atraafTypeOption
          .filter((atraaf) => atraaf.lawazimRequired)
          .map((atraaf) => ({
            atraafId: atraaf.atraafId,
            atraafByPublicId: String(atraaf.publicId),
            atraafName: atraaf.atraafName,
            infantCharge: 0,
            childCharge: 0,
            adultCharge: 0,
            seniorCharge: 0,
            extraCharge: 0,
          })),
      }));
    }
  }, [atraafTypeOption, formData.atraafCharges]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      additionalServiceCharges: chargeableServices.map((service) => {
        const existing = prev.additionalServiceCharges.find(
          (charge) => charge.tourServiceId === service.tourServiceId,
        );
        return existing || {
          tourServiceId: service.tourServiceId,
          serviceName: service.serviceName,
          tourServicePublicId: service?.publicId,
          infantCharge: 0,
          childCharge: 0,
          adultCharge: 0,
          seniorCharge: 0,
          extraCharge: 0,
        };
      }),
    }));
  }, [chargeableServices]);

  useEffect(() => {
    if (portOption.length > 0 && formData.arrivalCharges.length === 0) {
      setFormData((prev) => ({
        ...prev,
        arrivalCharges: portOption.map((port) => ({
          portId: String(port.portId),
          publicId: String(port.publicId),
          portName: port.portName,
          infantCharge: 0,
          childCharge: 0,
          adultCharge: 0,
          seniorCharge: 0,
          extraCharge: 0,
        })),
        departureCharges: portOption.map((port) => ({
          portId: String(port.portId),
          publicId: String(port.publicId),
          portName: port.portName,
          infantCharge: 0,
          childCharge: 0,
          adultCharge: 0,
          seniorCharge: 0,
          extraCharge: 0,
        })),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portOption]);

  const selectedProperty = useMemo(
    () =>
      propertyOptions.find(
        (property) => formData.propertyIds?.[0] === property.publicId,
      ),
    [formData.propertyIds, propertyOptions],
  );

  const selectedProperties = useMemo(
    () =>
      propertyOptions.filter((property) =>
        formData.propertyIds?.includes(property.publicId),
      ),
    [formData.propertyIds, propertyOptions],
  );

  const displayRoomTypeCharges = useMemo(() => {
    return formData.roomTypeCharges || [];
  }, [formData.roomTypeCharges]);

  const updateCancellationTier = (
    tierId: string,
    field: keyof CancellationTier,
    value: string | number,
  ) => {
    setFormData((prev) => {
      const tiers = (
        prev.cancellationPolicyConfig?.tiers ||
        defaultCancellationPolicyConfig.tiers
      ).map((tier) =>
        tier.id === tierId
          ? {
            ...tier,
            [field]:
              field === "penaltyBasis" ? value : Number(value || 0),
          }
          : tier,
      )

      if (
        field === "penaltyBasis" &&
        tiers.every((tier) => Boolean(tier.penaltyBasis))
      ) {
        setErrors((previousErrors) => ({
          ...previousErrors,
          cancellationPolicy: "",
        }));
      }

      return {
        ...prev,
        cancellationPolicyConfig: {
          ...(prev.cancellationPolicyConfig || defaultCancellationPolicyConfig),
          tiers,
        },
      };
    });
  };

  const updateCancellationPolicy = <K extends keyof CancellationPolicyConfig>(
    field: K,
    value: CancellationPolicyConfig[K],
  ) => {
    setFormData((prev) => ({
      ...prev,
      cancellationPolicyConfig: {
        ...(prev.cancellationPolicyConfig || defaultCancellationPolicyConfig),
        [field]: value,
      },
    }));
  };

  const addCancellationTier = () => {
    setFormData((prev) => ({
      ...prev,
      cancellationPolicyConfig: {
        ...(prev.cancellationPolicyConfig || defaultCancellationPolicyConfig),
        tiers: [
          ...(prev.cancellationPolicyConfig?.tiers ||
            defaultCancellationPolicyConfig.tiers),
          createCancellationTier(0, 0),
        ],
      },
    }));
  };

  const removeCancellationTier = (tierId: string) => {
    setFormData((prev) => {
      const currentTiers =
        prev.cancellationPolicyConfig?.tiers || defaultCancellationPolicyConfig.tiers;

      if (currentTiers.length <= 1) {
        toast.error("At least one cancellation tier is required");
        return prev;
      }

      return {
        ...prev,
        cancellationPolicyConfig: {
          ...(prev.cancellationPolicyConfig || defaultCancellationPolicyConfig),
          tiers: currentTiers.filter((tier) => tier.id !== tierId),
        },
      };
    });
  };

  const updateRoomTypeCharge = (
    propertyRoomTypePublicId: string,
    field: keyof RoomTypeCharges,
    value: number,
    propertyPublicId: string,
  ) => {
    // The table is rendered from propertyRoomTypes, so update it first to keep
    // the input value and the status badge in sync with the entered amount.
    setPropertyRoomTypes((previousProperties) =>
      previousProperties.map((property) =>
        property.propertyPublicId !== propertyPublicId
          ? property
          : {
            ...property,
            roomTypes: property.roomTypes.map((room) =>
              room.propertyRoomTypePublicId === propertyRoomTypePublicId
                ? { ...room, [field]: value }
                : room,
            ),
          },
      ),
    );

    setFormData((prev) => {
      const currentCharges = prev.roomTypeCharges || [];
      const property = propertyOptions.find(
        (item) => item.publicId === propertyPublicId,
      );
      const room = propertyRoomTypes
        .find((item) => item.propertyPublicId === propertyPublicId)
        ?.roomTypes.find(
          (item) =>
            item.propertyRoomTypePublicId === propertyRoomTypePublicId,
        );

      const chargeIndex = currentCharges.findIndex(
        (charge) =>
          charge.roomPublicId === propertyRoomTypePublicId,
      );

      let updatedRoomTypeCharges: RoomTypeCharges[];

      if (chargeIndex >= 0) {
        updatedRoomTypeCharges = currentCharges.map((charge, index) =>
          index === chargeIndex
            ? { ...charge, [field]: value }
            : charge,
        );
      } else {
        updatedRoomTypeCharges = [
          ...currentCharges,
          {
            roomTypeId: room?.roomTypeId || propertyRoomTypePublicId,
            roomPublicId: propertyRoomTypePublicId,
            roomTypeCode: room?.roomTypeCode,
            roomTypeName: room?.roomTypeName || "",
            propertyId: propertyPublicId,
            propertyName: property?.propertyName,
            basePrice: 0,
            earlyCheckInPricePerHour: 0,
            lateCheckOutPricePerHour: 0,
            extraCharge: 0,
            [field]: value,
          },
        ];
      }

      // ✅ Clear validation if at least one room type is configured
      const hasConfiguredRoomType = updatedRoomTypeCharges.some(
        (room) =>
          Number(room.basePrice) > 0 ||
          Number(room.earlyCheckInPricePerHour) > 0 ||
          Number(room.lateCheckOutPricePerHour) > 0 ||
          Number(room.extraCharge) > 0,
      );

      if (hasConfiguredRoomType) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          roomTypeConfiguration: "",
        }));
      }

      return {
        ...prev,
        roomTypeCharges: updatedRoomTypeCharges,
      };
    });
  };


  const addInterVenueCharge = () => {
    setInterVenueCharges((prev) => [...prev, createInterVenueChargeRow()]);
  };


  const updateInterVenueCharge = (
    id: string,
    field: keyof Omit<InterVenueChargeRow, "id">,
    value: string,
  ) => {
    const amountFields = [
      "infantCharge",
      "childCharge",
      "adultCharge",
      "seniorCharge",
      "extraCharge",
    ];

    const nextValue = amountFields.includes(field)
      ? parseFloat(value) || 0
      : value;

    setInterVenueCharges((prev) => {
      const updated = prev.map((row) =>
        row.id === id ? { ...row, [field]: nextValue } : row
      );

      // Clear validation when at least one row is configured
      const hasConfigured = updated.some(
        (row) =>
          Number(row.infantCharge) > 0 ||
          Number(row.childCharge) > 0 ||
          Number(row.adultCharge) > 0 ||
          Number(row.seniorCharge) > 0 ||
          Number(row.extraCharge) > 0
      );

      if (hasConfigured) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          interVenueCharges: "",
        }));
      }

      return updated;
    });
  };

  const removeInterVenueCharge = (id: string) => {
    setInterVenueCharges((prev) => prev.filter((row) => row.id !== id));
  };

  const requestDeleteConfirmation = (target: DeleteConfirmTarget) => {
    setDeleteConfirmTarget(target);
  };

  const cancelDeleteConfirmation = () => {
    setDeleteConfirmTarget(null);
  };

  const confirmDelete = () => {
    if (!deleteConfirmTarget) return;

    if (deleteConfirmTarget.type === "cancellation-tier") {
      removeCancellationTier(deleteConfirmTarget.id);
    }

    if (deleteConfirmTarget.type === "inter-venue-charge") {
      removeInterVenueCharge(deleteConfirmTarget.id);
    }

    setDeleteConfirmTarget(null);
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};

    // BASIC FORM VALIDATION
    if (!formData.code.trim()) {
      newErrors.code = "Code is required";
    }

    if (!formData.lawazimName.trim()) {
      newErrors.lawazimName = "Lawazim name is required";
    }

    if (!formData.dateFrom) {
      newErrors.dateFrom = "Valid from date is required";
    }

    if (!formData.dateTo) {
      newErrors.dateTo = "Valid to date is required";
    }

    if (formData.chargeType === "daily-rate" && !formData.dailyRateType) {
      newErrors.dailyRateType = "Calculation method is required";
    }

    if (formData.chargeType === "fixed-rate" && !formData.maxDays) {
      newErrors.maxDays = "Max days is required";
    }

    // PROPERTY VALIDATION
    if (!formData.forMiqaat && (!formData.propertyIds || formData.propertyIds.length === 0)) {
      newErrors.property = "Please select a property";
    }

    // ROOM TYPE CHARGES VALIDATION FOR SET NOTSET
    if (!formData.forMiqaat && (formData.propertyIds ?? []).length > 0) {
      const hasConfiguredRoomType = displayRoomTypeCharges.some((room) =>
        Number(room.basePrice) > 0 ||
        Number(room.earlyCheckInPricePerHour) > 0 ||
        Number(room.lateCheckOutPricePerHour) > 0 ||
        Number(room.extraCharge) > 0
      );

      if (!hasConfiguredRoomType) {
        newErrors.roomTypeConfiguration =
          "Please configure at least one room type.";
      }
    }

    if (
      !formData.forMiqaat &&
      formData.cancellationPolicyConfig?.tiers.some((tier) => !tier.penaltyBasis)
    ) {
      newErrors.cancellationPolicy = "Please select Based On for every cancellation tier.";
    }

    // INTER VENUE CHARGES VALIDATION FOR DUPLICATE ENTRIES
    // Inter Venue Charges validation
    const hasConfiguredInterVenueCharge = interVenueCharges.some(
      (row) =>
        Number(row.infantCharge) > 0 ||
        Number(row.childCharge) > 0 ||
        Number(row.adultCharge) > 0 ||
        Number(row.seniorCharge) > 0 ||
        Number(row.extraCharge) > 0
    );

    if (!hasConfiguredInterVenueCharge) {
      newErrors.interVenueCharges =
        "Please configure at least one Inter Venue charge.";
    }


    const venuePairs = new Set<string>();
    let hasDuplicateInterVenue = false;

    for (const row of interVenueCharges) {
      if (!row.fromVenueId || !row.toVenueId) continue;
      const key = `${row.fromVenueId}-${row.toVenueId}`;
      if (venuePairs.has(key)) {
        hasDuplicateInterVenue = true;
        break;
      }
      venuePairs.add(key);
    }

    if (hasDuplicateInterVenue) {
      newErrors.interVenueCharges =
        "Duplicate Inter Venue Charge is not allowed.";
    }
    // INTER VENUE CHARGES VALIDATION

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };




  const handleSave = async () => {
    if (!validateForm()) return;

    if (!formData.lawazimName.trim()) {
      toast.error("Please enter lawazim name");
      return;
    }

    try {
      const policy = formData.cancellationPolicyConfig;
      const payload: CreateLawazimPayload = {
        // ...(formData.forMiqaat
        //   ? {}
        //   : { propertyPublicId: formData.propertyIds?.[0] || "" }),
        propertyPublicIds: formData.propertyIds || [],
        code: formData.code,
        name: formData.lawazimName,
        isMiqaat: formData.forMiqaat,
        // Daily Rate = 1, Fixed Rate = 2
        chargeType: formData.chargeType === "daily-rate" ? 1 : 2,
        // 24 Hours = 1, Check In/Out = 2
        calculationMethod: formData.dailyRateType === "24-hours" ? 1
          : formData.dailyRateType === "check-in-checkout" ? 2 : 1,

        maxDays: formData.maxDays || 0,
        validFrom: String(formData.dateFrom),
        validTo: String(formData.dateTo),
        isActive: true,
        createdBy: 0,

        // ROOM TYPE PAYLOAD SECTION
        // ONLY FOR NON-MIQAAT
        ...(!formData.forMiqaat && {
          properties: propertyRoomTypes.map((property) => ({
            propertyPublicId: property.propertyPublicId,
            roomTypePrices: (property.roomTypes || []).map((room) => ({
              propertyRoomTypePublicId: room.propertyRoomTypePublicId,
              basePrice: Number(room.basePrice || 0),
              earlyCheckInPrice: Number(room.earlyCheckInPricePerHour || 0),
              lateCheckOutPrice: Number(room.lateCheckOutPricePerHour || 0),
              extraCharges: Number(room.extraCharge || 0),
            })),
          })),

          cancellationPolicy: {
            freeCancelHours: Number(policy?.freeCancellationHours || 0),
            tiers: policy?.tiers.map((tier) => ({
              // cancellationTierPublicId: "",
              daysBeforeCheckIn: Number(tier.daysBeforeCheckIn),
              penaltyValue: Number(tier.penaltyPercentage),
              basedOn: Number(tier.penaltyBasis)
            })),
          },
        }),

        // PROPERTY TYPE PAYLOAD SECTION
        // ONLY WHEN FOR MIQAAT = TRUE PROPERTY TYPE CONFIGURATION
        ...(formData.forMiqaat && {
          propertyTypePrices: formData.propertyTypeCharges.map((item) => ({
            propertyTypePublicId: item.propertyTypePublicId,
            infantPrice: Number(item.infantCharge || 0),
            childPrice: Number(item.childCharge || 0),
            adultPrice: Number(item.adultCharge || 0),
            seniorPrice: Number(item.seniorCharge || 0),
            extraCharge: Number(item.extraCharge || 0),
          })),
        }),

        // ATRAAF SERVICES PAYLOAD SECTION
        atraafPrices: formData.atraafCharges.map((item) => ({
          atraafId: item.atraafId,
          atraafPublicId: item.atraafByPublicId,
          infantPrice: Number(item.infantCharge || 0),
          childPrice: Number(item.childCharge || 0),
          adultPrice: Number(item.adultCharge || 0),
          seniorPrice: Number(item.seniorCharge || 0),
          extraCharge: Number(item.extraCharge || 0),
        })),

        // ADDITIONAL SERVICES PAYLOAD
        additionalServicePrices: formData.additionalServiceCharges.map((item) => ({
          tourServicePublicId: item.tourServicePublicId,
          infantPrice: Number(item.infantCharge || 0),
          childPrice: Number(item.childCharge || 0),
          adultPrice: Number(item.adultCharge || 0),
          seniorPrice: Number(item.seniorCharge || 0),
          extraCharge: Number(item.extraCharge || 0),
        })),

        // TRANSPORT SERVICES ARRIAL & DEPARTURE PAYLOAD
        transportPrices: [
          ...formData.arrivalCharges.map((item) => ({
            portPublicId: item?.publicId,
            transportType: 1, // Arrival
            transportTypeName: "Arrival",
            infantPrice: Number(item.infantCharge || 0),
            childPrice: Number(item.childCharge || 0),
            adultPrice: Number(item.adultCharge || 0),
            seniorPrice: Number(item.seniorCharge || 0),
            extraCharge: Number(item.extraCharge || 0),
          })),

          ...formData.departureCharges.map((item) => ({
            portPublicId: item?.publicId,
            transportType: 2, // Departure
            transportTypeName: "Departure",
            infantPrice: Number(item.infantCharge || 0),
            childPrice: Number(item.childCharge || 0),
            adultPrice: Number(item.adultCharge || 0),
            seniorPrice: Number(item.seniorCharge || 0),
            extraCharge: Number(item.extraCharge || 0),
          })),
        ],

        // INTERVENUE PAYLOAD SECTION
        interVenueTransportPrices: interVenueCharges.map((item) => ({
          fromVenuePublicId: item.fromVenueId,
          toVenuePublicId: item.toVenueId,
          infantPrice: Number(item.infantCharge || 0),
          childPrice: Number(item.childCharge || 0),
          adultPrice: Number(item.adultCharge || 0),
          seniorPrice: Number(item.seniorCharge || 0),
          extraCharge: Number(item.extraCharge || 0),
        })),

        // EXTERNAL HOTEL CHARGES
        // externalPropertyPrices
        externalPropertyPrices: externalHotelCharges.map((item) => ({
          // propertyPublicId: item?.propertyPublicId || null,
          propertyPublicId: null,
          infantPrice: Number(item.infantCharge || 0),
          childPrice: Number(item.childCharge || 0),
          adultPrice: Number(item.adultCharge || 0),
          seniorPrice: Number(item.seniorCharge || 0),
          extraCharge: Number(item.extraCharge || 0),
        })),
      };

      // console.log("Create Lawazim payload:", payload);
      //API CALL
      const response = await createLawazimConfig(payload);
      if (!response.success) {
        toast.error(response.message || "Failed to create lawazim");
        return;
      }
      toast.success(response.message || "Lawazim created successfully");
      resetForm();
      navigate("/lawazim-master");
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to create lawazim";
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };


  const renderMoneyInput = (
    value: number | undefined,
    onChange: (value: number) => void,
  ) => (
    <Input
      type="number"
      step="0.01"
      min="0"
      placeholder="0.00"
      value={value ?? 0}
      onChange={(event) => onChange(parseFloat(event.target.value) || 0)}
      className="mx-auto h-8 max-w-[180px] rounded-md border-gray-200 bg-white text-center text-sm"
    />
  );

  const resetForm = () => {
    setExternalHotelCharges([createExternalHotelChargeRow()]);
    setFormData({
      code: "",
      lawazimName: "",
      forMiqaat: false,
      chargeType: "daily-rate",
      dailyRateType: undefined,
      dateFrom: "",
      dateTo: "",
      maxDays: undefined,
      roomTypeCharges: fallbackRoomTypes,
      propertyTypeCharges: propertyTypeOption.map((propType) => ({
        propertyTypeId: String(propType.id),
        propertyTypeName: propType.name,
        infantCharge: 0,
        childCharge: 0,
        adultCharge: 0,
        seniorCharge: 0,
        extraCharge: 0,
      })),
      additionalServiceCharges: chargeableServices.map((service) => ({
        tourServiceId: service.tourServiceId,
        tourServicePublicId: service.publicId,
        serviceName: service.serviceName,
        infantCharge: 0,
        childCharge: 0,
        adultCharge: 0,
        seniorCharge: 0,
        extraCharge: 0,
      })),
      atraafCharges: atraafTypeOption
        .filter((atraf) => atraf.lawazimRequired)
        .map((atraf) => ({
          atrafId: atraf.atraafId,
          atrafName: atraf.atraafName,
          infantCharge: 0,
          childCharge: 0,
          adultCharge: 0,
          seniorCharge: 0,
          extraCharge: 0,
        })),
      arrivalCharges: portOption.map((port) => ({
        portId: String(port.portId),
        portName: port.portName,
        infantCharge: 0,
        childCharge: 0,
        adultCharge: 0,
        seniorCharge: 0,
        extraCharge: 0,
      })),
      departureCharges: portOption.map((port) => ({
        portId: String(port.portId),
        portName: port.portName,
        infantCharge: 0,
        childCharge: 0,
        adultCharge: 0,
        seniorCharge: 0,
        extraCharge: 0,
      })),
    });
  };

  // SHOWING ERROR COUNT INSIDE TABS
  const roomTypeErrorCount = [
    errors.property,
    errors.roomTypeConfiguration,
  ].filter(Boolean).length +
    (errors.cancellationPolicy
      ? (formData.cancellationPolicyConfig?.tiers || []).filter(
        (tier) => !tier.penaltyBasis,
      ).length
      : 0);

  const servicesErrorCount = [
    errors.interVenueCharges,
  ].filter(Boolean).length;
  // SHOWING ERROR COUNT INSIDE TABS


  return (
    <div className="lawazim-add-config pb-8">
      <style>
        {`
          .lawazim-add-config input[type="number"] {
            appearance: textfield;
            -moz-appearance: textfield;
          }

          .lawazim-add-config input[type="number"]::-webkit-inner-spin-button,
          .lawazim-add-config input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
        `}
      </style>
      {loading && <FullScreenLoading />}

      {/* Header */}
      {/* <div className="mb-4">
        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              resetForm();
              navigate(-1);
            }}
            variant="ghost"
            size="sm"
            className="text-[#5B9BD5] hover:bg-[#5B9BD5]/10 -ml-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1
              style={{ color: "#5B9BD5" }}
              className="flex items-center gap-2"
            >
              <Package className="w-6 h-6" />
              {"Add Lawazim Configuration"}
            </h1>
          </div>
        </div>
      </div> */}

      <Card
        style={{ backgroundColor: "#E3F2FD" }}
        className="bg-white border-[#5B9BD5]/20 shadow-md rounded-t-xl"
      >
        {/* <CardHeader className="">
          <CardTitle className="text-gray-900 text-base">
            Lawazim Fee Configuration
          </CardTitle>
        </CardHeader> */}
        <CardContent className="bg-white p-4 space-y-4 rounded-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <InputFieldset
                label="Code"
                required
                value={formData.code}
                onChange={(e) => {
                  setFormData({ ...formData, code: e.target.value });
                  setErrors({ ...errors, code: "" });
                }}
                placeholder="e.g., STDPKG, PRMPKG"
                className="bg-white border-gray-200"
              />
              {errors.code && (
                <p className="text-red-500 text-xs mt-1">{errors.code}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <InputFieldset
                label="Lawazim Name"
                required
                value={formData.lawazimName}
                onChange={(e) => {
                  setFormData({ ...formData, lawazimName: e.target.value });
                  setErrors({ ...errors, lawazimName: "" });
                }}
                placeholder="Enter lawazim name..."
                className="bg-white border-gray-200"
              />
              {errors.lawazimName && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.lawazimName}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">
                Valid From <span className="text-red-500">*</span>
              </Label>

              <div className="flex h-11 items-center rounded-xl border border-gray-200 bg-white px-2 shadow-sm">
                <Input
                  type="date"
                  value={formData.dateFrom || ""}
                  onChange={(event) => {
                    setFormData({ ...formData, dateFrom: event.target.value });
                    setErrors({ ...errors, dateFrom: "" });
                  }}
                  className="border-0 p-0 shadow-none focus-visible:ring-0"
                />
              </div>

              {errors.dateFrom && (
                <p className="text-xs text-red-500">{errors.dateFrom}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">
                Valid To <span className="text-red-500">*</span>
              </Label>

              <div className="flex h-11 items-center rounded-xl border border-gray-200 bg-white px-2 shadow-sm">
                <Input
                  type="date"
                  value={formData.dateTo || ""}
                  onChange={(event) => {
                    setFormData({ ...formData, dateTo: event.target.value });
                    setErrors({ ...errors, dateTo: "" });
                  }}
                  className="border-0 p-0 shadow-none focus-visible:ring-0"
                />
              </div>

              {errors.dateTo && (
                <p className="text-xs text-red-500">{errors.dateTo}</p>
              )}
            </div>
          </div>
          {/* Charge Type */}
          <div
            className={`grid grid-cols-1 gap-2 ${formData.chargeType === "daily-rate"
              ? "xl:grid-cols-[0.8fr_1.2fr_1.5fr]"
              : "xl:grid-cols-[0.8fr_1.2fr_1fr]"
              }`}
          >
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">
                Charge Type <span className="text-red-500">*</span>
              </Label>
              <RadioGroup
                value={formData.chargeType}
                onValueChange={(value: "daily-rate" | "fixed-rate") =>
                  setFormData({
                    ...formData,
                    chargeType: value,
                    dailyRateType:
                      value === "daily-rate" ? formData.dailyRateType : undefined,
                    maxDays: value === "fixed-rate" ? formData.maxDays : undefined,
                  })
                }
                className="flex"
                style={{ gap: "8px" }}
              >
                <label
                  className={`flex h-11 flex-1 items-center justify-start gap-2 rounded-xl border border-gray-200 bg-white px-2 shadow-sm ${!formData.forMiqaat ? "min-w-[220px]" : ""
                    }`}
                >
                  <RadioGroupItem value="daily-rate" id="daily-rate" />
                  <span className="font-semibold whitespace-nowrap text-sm">
                    Daily Rate
                  </span>
                </label>
                {formData.forMiqaat && (
                  <label
                    className={`flex h-11 flex-1 items-center justify-start gap-2 rounded-xl border border-gray-200 bg-white px-2 shadow-sm ${!formData.forMiqaat ? "min-w-[220px]" : ""
                      }`}
                  >
                    <RadioGroupItem value="fixed-rate" id="fixed-rate" />
                    <span className="font-semibold whitespace-nowrap text-sm">
                      Fixed Rate
                    </span>
                  </label>
                )}
              </RadioGroup>
              {/* {errors.chargeType && (
                <p className="text-xs text-red-500">{errors.chargeType}</p>
              )} */}
            </div>

            {formData.chargeType === "fixed-rate" && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-900">
                  Max Days <span className="text-red-500">*</span>
                </Label>

                <div className="flex h-11 items-center rounded-xl border border-gray-200 bg-white px-2 shadow-sm">
                  <Input
                    type="number"
                    min="1"
                    value={formData.maxDays || ""}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        maxDays: parseInt(event.target.value) || undefined,
                      });
                      setErrors({ ...errors, maxDays: "" });
                    }}
                    placeholder="e.g. 14"
                    className="border-0 p-0 shadow-none focus-visible:ring-0"
                  />
                </div>

                {errors.maxDays && (
                  <p className="text-xs text-red-500">{errors.maxDays}</p>
                )}
              </div>
            )}

            {formData.chargeType === "daily-rate" && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-900">
                  Calculation Method <span className="text-red-500">*</span>
                </Label>
                <RadioGroup
                  value={formData.dailyRateType || ""}
                  onValueChange={(value: "24-hours" | "check-in-checkout") => {
                    setFormData({ ...formData, dailyRateType: value })
                    setErrors({ ...errors, dailyRateType: "" });
                  }}
                  className="grid grid-cols-[minmax(105px,0.8fr)_minmax(190px,1.2fr)]"
                  style={{ gap: "8px" }}
                >
                  <label className="flex h-11 cursor-pointer items-center gap-2 rounded-xl border border-gray-200 bg-white px-2 text-sm shadow-sm">
                    <RadioGroupItem value="24-hours" id="24-hours" />
                    <span className="font-semibold whitespace-nowrap text-sm">
                      24 Hours
                    </span>
                  </label>
                  <label className="flex h-11 cursor-pointer items-center gap-2 rounded-xl border border-gray-200 bg-white px-2 text-sm shadow-sm">
                    <RadioGroupItem
                      value="check-in-checkout"
                      id="check-in-checkout"
                    />
                    <span className="font-semibold whitespace-nowrap text-sm">
                      Check-in/Checkout
                    </span>
                  </label>
                </RadioGroup>
                {errors?.dailyRateType && (
                  <p className="text-xs text-red-500">{errors.dailyRateType}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-900">
                For Miqaat?
              </Label>
              <label className="flex h-11 items-center gap-3 px-1 text-sm text-gray-700">
                <Checkbox
                  checked={formData.forMiqaat}
                  onCheckedChange={(checked) => {
                    const isMiqaat = Boolean(checked);
                    setPropertyPickerOpen(false);
                    setMiqaatAccommodationMode("property-type");
                    setActiveConfigTab(isMiqaat ? "property-type" : "room-type");
                    setFormData({
                      ...formData,
                      forMiqaat: isMiqaat,
                      propertyIds: propertyOptions.map(
                        (property) => property.publicId,
                      ),
                      chargeType: isMiqaat
                        ? formData.chargeType
                        : formData.chargeType === "fixed-rate"
                          ? "daily-rate"
                          : formData.chargeType,
                      maxDays: isMiqaat ? formData.maxDays : undefined,
                    });
                  }}
                />
                Yes
              </label>
            </div>
          </div>
          {/* Charges Table */}


          <div className="border-t-2 border-[#5B9BD5]/20 pt-4">
            <Tabs
              value={activeConfigTab}
              onValueChange={setActiveConfigTab}
              className="w-full"
            >
              <TabsList className="grid h-auto w-full grid-cols-1 rounded-xl border border-[#5B9BD5]/20 bg-sky-50/80 p-1 gap-1 sm:grid-cols-2">
                {(!formData.forMiqaat || miqaatAccommodationMode === "room-type") && (
                  <TabsTrigger
                    value="room-type"
                    className="flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#5B9BD5]"
                  >
                    <BedDouble className="h-4 w-4 shrink-0" />
                    <span>Room Type Configuration</span>
                    {/* SHOWING TAB ERROR COUNT INSIDE TAB */}
                    {roomTypeErrorCount > 0 && (
                      <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-2 text-xs font-semibold text-white">
                        {roomTypeErrorCount}
                      </span>
                    )}
                    {/* SHOWING TAB ERROR COUNT INSIDE TAB */}
                  </TabsTrigger>
                )}

                {formData.forMiqaat && miqaatAccommodationMode === "property-type" && (
                  <TabsTrigger
                    value="property-type"
                    className="flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#5B9BD5]"
                  >
                    <Building2 className="h-4 w-4 shrink-0" />
                    <span>Property Type Configuration</span>
                  </TabsTrigger>
                )}

                <TabsTrigger
                  value="services"
                  className="flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#5B9BD5]"
                >
                  <Settings2 className="h-4 w-4 shrink-0" />

                  <span>Services</span>
                  {/* SHOWING TAB ERROR COUNT INSIDE TAB */}
                  {servicesErrorCount > 0 && (
                    <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-2 text-xs font-semibold text-white">
                      {servicesErrorCount}
                    </span>
                  )}
                  {/* SHOWING TAB ERROR COUNT INSIDE TAB */}
                </TabsTrigger>
              </TabsList>

              {(!formData.forMiqaat || miqaatAccommodationMode === "room-type") && (
                <TabsContent value="room-type" className="mt-5 space-y-5">
                  {/* <Card className="overflow-hidden border-[#5B9BD5]/20 shadow-sm" style={{ gap: "0px" }}> */}
                  <Card className="relative z-20 w-full overflow-visible border-[#5B9BD5]/20 shadow-sm" style={{ gap: "0px" }}>
                    <Accordion type="single" defaultValue="room-type-charges" collapsible>
                      <AccordionItem value="room-type-charges" className="border-0">
                        <CardHeader className="border-b border-yellow-100 bg-yellow-50/70 px-5 py-3">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <AccordionTrigger className="pt-2 text-base font-semibold text-[#5B9BD5] hover:no-underline [&>svg]:mt-1">
                              <div>
                                Room Type Charges{" "}
                                <span className="ml-2 text-sm font-medium text-gray-500">
                                  (night charges per room type)
                                </span>
                              </div>
                            </AccordionTrigger>
                            <div className="relative w-full lg:w-[360px]">
                              <button
                                type="button"
                                onClick={() => setPropertyPickerOpen((open) => !open)}
                                className="flex h-11 w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 text-left text-sm text-gray-700 shadow-sm"
                              >
                                <span className="truncate">
                                  {selectedProperties.length > 0
                                    ? selectedProperties.length + " properties selected"
                                    : "Select properties..."}
                                </span>
                                <ChevronDown className="h-4 w-4 text-gray-500" />
                              </button>
                              {propertyPickerOpen && (
                                <div className="absolute right-0 top-12 z-20 max-h-56 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
                                  {propertyOptions.map((property) => {
                                    const isSelected = true;

                                    return (
                                      <button
                                        key={property.publicId}
                                        type="button"
                                        onClick={() => undefined}
                                        className={
                                          "flex w-full cursor-default items-center justify-between rounded-lg px-2 py-2 text-left " +
                                          (isSelected
                                            ? "bg-sky-50 text-[#5B9BD5]"
                                            : "text-gray-800")
                                        }
                                      >
                                        <span className="text-sm">
                                          {property.propertyName}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              {errors.property && (
                                <p className="mt-1 text-xs text-red-500">
                                  {errors.property}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <AccordionContent className="overflow-visible pb-0">
                          <CardContent style={{ padding: "0px" }}>
                            {selectedProperties.length > 0 ? (
                              propertyOptions.length === 0 ? (
                                <div className="p-6 text-center text-sm text-gray-500">
                                  No active properties found in Property Detail Master
                                </div>
                              ) : (
                                <Accordion
                                  key={selectedProperties
                                    .map((property) => property.publicId)
                                    .join("|")}
                                  type="multiple"
                                  defaultValue={selectedProperties.map(
                                    (property) => property.publicId,
                                  )}
                                  className="mt-4 mb-2 mx-4 divide-y divide-sky-100 rounded-lg border border-sky-100 bg-white"
                                >
                                  {selectedProperties.map((property) => {
                                    // const propertyRooms = displayRoomTypeCharges.filter(
                                    //   (room) => room.propertyId === property.publicId,
                                    // );
                                    const propertyData = propertyRoomTypes.find(
                                      (item) => item.propertyPublicId === property.publicId,
                                    );

                                    const propertyRooms = propertyData?.roomTypes ?? [];

                                    return (
                                      <AccordionItem
                                        key={property.publicId}
                                        value={property.publicId}
                                        className="border-0"
                                      >
                                        <AccordionTrigger className="border-l-4 border-l-sky-200 px-4 py-3 text-left hover:no-underline hover:bg-sky-50/70 [&>svg]:ml-auto">
                                          <div className="flex min-w-0 items-center gap-3">
                                            <Building2 className="h-4 w-4 shrink-0 text-[#5B9BD5]" />
                                            <span className="truncate text-sm font-semibold text-[#5B9BD5]">
                                              {property.propertyName}
                                            </span>
                                          </div>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                          <div>
                                            <table className="w-full table-fixed">
                                              <thead>
                                                <tr className="border-b border-yellow-200 bg-yellow-50/40">
                                                  <th className="w-[22%] p-3 text-left text-sm font-semibold text-gray-900">
                                                    Room Type
                                                  </th>
                                                  <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                                    Night Charges
                                                  </th>
                                                  <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                                    Early Check-in
                                                  </th>
                                                  <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                                    Late Checkout
                                                  </th>
                                                  <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                                    Extra Charges
                                                  </th>
                                                  <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                                    Status
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {propertyRooms.map((room, index) => {
                                                  const isSet =
                                                    room.basePrice > 0 ||
                                                    room.earlyCheckInPricePerHour > 0 ||
                                                    room.lateCheckOutPricePerHour > 0 ||
                                                    Number(room.extraCharge || 0) > 0;

                                                  return (
                                                    <tr
                                                      key={
                                                        property.publicId +
                                                        "-" +
                                                        room.roomTypeId
                                                      }
                                                      className={
                                                        "border-b border-gray-100 " +
                                                        (index % 2 === 0
                                                          ? "bg-white"
                                                          : "bg-gray-50/40")
                                                      }
                                                    >
                                                      <td className="p-3 text-sm text-gray-900">
                                                        <span className="mr-2 font-semibold text-gray-500">
                                                          {room.roomTypeCode}
                                                        </span>
                                                        <span className="font-semibold">
                                                          {room.roomTypeName}
                                                        </span>
                                                        {/* {(room.minOccupancy ||
                                                          room.maxOccupancy) && (
                                                            <span className="ml-2 text-gray-500">
                                                              ({room.minOccupancy || 1}-
                                                              {room.maxOccupancy || 1}{" "}
                                                              pax)
                                                            </span>
                                                          )} */}
                                                      </td>
                                                      <td className="p-3">
                                                        {renderMoneyInput(
                                                          room.basePrice,
                                                          (value) =>
                                                            updateRoomTypeCharge(
                                                              room.propertyRoomTypePublicId,
                                                              "basePrice",
                                                              value,
                                                              property.publicId,
                                                            ),
                                                        )}
                                                      </td>
                                                      <td className="p-3">
                                                        {renderMoneyInput(
                                                          room.earlyCheckInPricePerHour,
                                                          (value) =>
                                                            updateRoomTypeCharge(
                                                              room.propertyRoomTypePublicId,
                                                              "earlyCheckInPricePerHour",
                                                              value,
                                                              property.publicId,
                                                            ),
                                                        )}
                                                      </td>
                                                      <td className="p-3">
                                                        {renderMoneyInput(
                                                          room.lateCheckOutPricePerHour,
                                                          (value) =>
                                                            updateRoomTypeCharge(
                                                              room.propertyRoomTypePublicId,
                                                              "lateCheckOutPricePerHour",
                                                              value,
                                                              property.publicId,
                                                            ),
                                                        )}
                                                      </td>
                                                      <td className="p-3">
                                                        {renderMoneyInput(
                                                          room.extraCharge || 0,
                                                          (value) =>
                                                            updateRoomTypeCharge(
                                                              room.propertyRoomTypePublicId,
                                                              "extraCharge",
                                                              value,
                                                              property.publicId,
                                                            ),
                                                        )}
                                                      </td>
                                                      <td className="p-3 text-center">
                                                        <span
                                                          className={
                                                            "inline-flex rounded-full px-3 py-1 text-xs font-semibold " +
                                                            (isSet
                                                              ? "bg-green-50 text-green-700"
                                                              : "bg-gray-100 text-gray-500")
                                                          }
                                                        >
                                                          {isSet ? "Set" : "Not Set"}
                                                        </span>
                                                      </td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                        </AccordionContent>
                                      </AccordionItem>
                                    );
                                  })}
                                </Accordion>
                              )
                            ) : !selectedProperty ? (
                              <div className="p-6 text-center text-sm text-gray-500">
                                Please select the property for configuration
                              </div>
                            ) : (
                              <div style={{ padding: "0px" }}>
                                <table className="w-full table-fixed">
                                  <thead>
                                    <tr className="border-b border-yellow-200 bg-yellow-50/40">
                                      <th className="p-3 text-left text-sm font-semibold text-gray-900">
                                        Room Type
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Night Charges
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Early Check-in
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Late Checkout
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Extra Charges
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Status
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr className="border-b border-sky-100 bg-sky-50/70">
                                      <td
                                        colSpan={6}
                                        className="p-3 text-sm text-gray-900 break-words font-semibold text-[#5B9BD5]"
                                      >
                                        {selectedProperty.propertyName}
                                      </td>
                                    </tr>
                                    {displayRoomTypeCharges.map((room, index) => {
                                      const isSet =
                                        room.basePrice > 0 ||
                                        room.earlyCheckInPricePerHour > 0 ||
                                        room.lateCheckOutPricePerHour > 0 ||
                                        Number(room.extraCharge || 0) > 0;

                                      return (
                                        <tr
                                          key={room.roomTypeId}
                                          className={
                                            "border-b border-gray-100 " +
                                            (index % 2 === 0
                                              ? "bg-white"
                                              : "bg-gray-50/40")
                                          }
                                        >
                                          <td className="p-3 text-sm text-gray-900">
                                            <span className="mr-2 font-semibold text-gray-500">
                                              {room.roomTypeCode}
                                            </span>
                                            <span className="font-semibold">
                                              {room.roomTypeName}
                                            </span>
                                            {(room.minOccupancy ||
                                              room.maxOccupancy) && (
                                                <span className="ml-2 text-gray-500">
                                                  ({room.minOccupancy || 1}-
                                                  {room.maxOccupancy || 1} pax)
                                                </span>
                                              )}
                                          </td>
                                          <td className="p-3">
                                            {renderMoneyInput(room.basePrice, (value) =>
                                              updateRoomTypeCharge(
                                                room.roomPublicId || room.roomTypeId,
                                                "basePrice",
                                                value,
                                                selectedProperty.publicId,
                                              ),
                                            )}
                                          </td>
                                          <td className="p-3">
                                            {renderMoneyInput(
                                              room.earlyCheckInPricePerHour,
                                              (value) =>
                                                updateRoomTypeCharge(
                                                  room.roomPublicId || room.roomTypeId,
                                                  "earlyCheckInPricePerHour",
                                                  value,
                                                  selectedProperty.publicId,
                                                ),
                                            )}
                                          </td>
                                          <td className="p-3">
                                            {renderMoneyInput(
                                              room.lateCheckOutPricePerHour,
                                              (value) =>
                                                updateRoomTypeCharge(
                                                  room.roomPublicId || room.roomTypeId,
                                                  "lateCheckOutPricePerHour",
                                                  value,
                                                  selectedProperty.publicId,
                                                ),
                                            )}
                                          </td>
                                          <td className="p-3">
                                            {renderMoneyInput(
                                              room.extraCharge || 0,
                                              (value) =>
                                                updateRoomTypeCharge(
                                                  room.roomPublicId || room.roomTypeId,
                                                  "extraCharge",
                                                  value,
                                                  selectedProperty.publicId,
                                                ),
                                            )}
                                          </td>
                                          <td className="p-3 text-center">
                                            <span
                                              className={
                                                "inline-flex rounded-full px-3 py-1 text-xs font-semibold " +
                                                (isSet
                                                  ? "bg-green-50 text-green-700"
                                                  : "bg-gray-100 text-gray-500")
                                              }
                                            >
                                              {isSet ? "Set" : "Not Set"}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            {errors.roomTypeConfiguration && (
                              <p className="pl-4 mt-1 text-sm text-red-600">
                                {errors.roomTypeConfiguration}
                              </p>
                            )}
                          </CardContent>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </Card>

                  <Card className="relative z-10 w-full overflow-visible border-sky-100 shadow-sm">
                    <Accordion
                      type="single"
                      defaultValue="external-hotels"
                      collapsible
                    >
                      <AccordionItem value="external-hotels" className="border-0">
                        <CardHeader className="border-b border-sky-100 bg-sky-50/70 px-2 py-2">
                          <AccordionTrigger className="w-full text-lg font-semibold text-[#5B9BD5] hover:no-underline">
                            <span>External Hotels</span>
                          </AccordionTrigger>
                        </CardHeader>
                        <AccordionContent className="pb-0">
                          <CardContent className="p-4 sm:p-4">
                            {/* comment it out but for future requirment it is neccessary */}
                            {/* <div className="mb-4 flex justify-end">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                  setExternalHotelCharges((previous) => [
                                    ...previous,
                                    createExternalHotelChargeRow(),
                                  ])
                                }
                                className="shrink-0 border-[#5B9BD5]/40 text-[#31547c]"
                              >
                                <Plus className="mr-2 h-4 w-4" />
                                Add More
                              </Button>
                            </div> */}
                            <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm lg:block">
                              <table className="w-full min-w-[1050px]">
                                <thead>
                                  <tr className="border-b border-gray-200 bg-gray-50">
                                    <th className="p-3 text-left text-sm font-semibold text-gray-900">
                                      Hotel Name
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Infant Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Child Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Adult Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Senior Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Extras
                                    </th>
                                    <th className="w-14 p-3" aria-label="Actions" />
                                  </tr>
                                </thead>
                                <tbody>
                                  {externalHotelCharges.map((row, index) => (
                                    <tr
                                      key={row.id}
                                      className={
                                        "border-b border-gray-100 last:border-b-0 " +
                                        (index % 2 === 0 ? "bg-white" : "bg-gray-50/40")
                                      }
                                    >
                                      <td className="p-3 cursor-not-allowed">
                                        <div className="p-2 rounded-lg border border-gray-300 bg-white px-2">
                                          <p>All</p>
                                          {/* comment it out but for future requirment it is neccessary */}
                                          {/* <Select
                                            value={row.propertyPublicId}
                                            onValueChange={(value) =>
                                              setExternalHotelCharges((previous) =>
                                                previous.map((item) =>
                                                  item.id === row.id
                                                    ? { ...item, propertyPublicId: value }
                                                    : item,
                                                ),
                                              )
                                            }
                                          >
                                            <SelectTriggerUI className="h-10 min-w-[210px] rounded-lg border-gray-200 bg-white">
                                              <SelectValue placeholder="Select Property" />
                                            </SelectTriggerUI>
                                            <SelectContent>
                                              {propertyOptions.map((property) => (
                                                <SelectItem
                                                  key={property.publicId}
                                                  value={property.publicId}
                                                >
                                                  {property.propertyName}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select> */}
                                        </div>
                                      </td>
                                      {([
                                        "infantCharge",
                                        "childCharge",
                                        "adultCharge",
                                        "seniorCharge",
                                        "extraCharge",
                                      ] as const).map((field) => (
                                        <td key={field} className="p-3">
                                          <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={row[field]}
                                            onChange={(event) =>
                                              setExternalHotelCharges((previous) =>
                                                previous.map((item) =>
                                                  item.id === row.id
                                                    ? {
                                                      ...item,
                                                      [field]: Math.max(
                                                        0,
                                                        Number(event.target.value || 0),
                                                      ),
                                                    }
                                                    : item,
                                                ),
                                              )
                                            }
                                            className="mx-auto h-10 min-w-[130px] rounded-lg border-gray-300 bg-gray-50 text-center"
                                          />
                                        </td>
                                      ))}
                                      {/* comment it out but for future requirment it is neccessary */}
                                      {/* <td className="p-3 text-center">
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          disabled={externalHotelCharges.length === 1}
                                          onClick={() =>
                                            setExternalHotelCharges((previous) =>
                                              previous.filter((item) => item.id !== row.id),
                                            )
                                          }
                                          className="text-red-500 hover:bg-red-50 hover:text-red-600"
                                          aria-label="Delete external hotel charge"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </td> */}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            <div className="space-y-4 lg:hidden">
                              {externalHotelCharges.map((row) => (
                                <div
                                  key={row.id}
                                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                                >
                                  <div className="space-y-4">
                                    <div className="space-y-2">
                                      <Label className="text-sm font-semibold text-gray-900">
                                        Hotel Name
                                      </Label>
                                      <Select
                                        value={row.propertyPublicId}
                                        onValueChange={(value) =>
                                          setExternalHotelCharges((previous) =>
                                            previous.map((item) =>
                                              item.id === row.id
                                                ? { ...item, propertyPublicId: value }
                                                : item,
                                            ),
                                          )
                                        }
                                      >
                                        <SelectTriggerUI className="h-11 w-full rounded-lg border-gray-300 bg-white">
                                          <SelectValue placeholder="Select hotel" />
                                        </SelectTriggerUI>
                                        <SelectContent>
                                          {propertyOptions.map((property) => (
                                            <SelectItem
                                              key={property.publicId}
                                              value={property.publicId}
                                            >
                                              {property.propertyName}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    {([
                                      ["infantCharge", "Infant Amount ($)"],
                                      ["childCharge", "Child Amount ($)"],
                                      ["adultCharge", "Adult Amount ($)"],
                                      ["seniorCharge", "Senior Amount ($)"],
                                      ["extraCharge", "Extras ($)"],
                                    ] as const).map(([field, label]) => (
                                      <div key={field} className="space-y-2">
                                        <Label className="text-sm font-semibold text-gray-900">
                                          {label}
                                        </Label>
                                        <Input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          placeholder="0.00"
                                          value={row[field]}
                                          onChange={(event) =>
                                            setExternalHotelCharges((previous) =>
                                              previous.map((item) =>
                                                item.id === row.id
                                                  ? {
                                                    ...item,
                                                    [field]: Math.max(
                                                      0,
                                                      Number(event.target.value || 0),
                                                    ),
                                                  }
                                                  : item,
                                              ),
                                            )
                                          }
                                          className="h-11 w-full rounded-lg border-gray-300 bg-gray-50"
                                        />
                                      </div>
                                    ))}

                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      disabled={externalHotelCharges.length === 1}
                                      onClick={() =>
                                        setExternalHotelCharges((previous) =>
                                          previous.filter((item) => item.id !== row.id),
                                        )
                                      }
                                      className="text-red-500 hover:bg-red-50 hover:text-red-600"
                                      aria-label="Delete external hotel charge"
                                    >
                                      <Trash2 className="h-5 w-5" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </Card>


                  <Card className="relative z-0 w-full overflow-visible border-sky-100 shadow-sm">
                    <Accordion type="single" defaultValue="undefined" collapsible>
                      <AccordionItem value="cancellation-policy" className="border-0">
                        <CardHeader className="bg-sky-50/70 px-5 py-4">
                          <AccordionTrigger className="text-lg font-semibold text-[#5B9BD5] hover:no-underline [&>svg]:mt-1">
                            <span>Cancellation Policy Configuration</span>
                          </AccordionTrigger>
                          {/* <p className="text-sm text-gray-600">
                            Define the free-cancellation window and how penalties are calculated.
                          </p> */}
                        </CardHeader>
                        <AccordionContent className="pb-0">
                          <CardContent className="p-5 space-y-5">
                            {(() => {
                              const policy = formData.cancellationPolicyConfig || defaultCancellationPolicyConfig;
                              return (
                                <>
                                  <div className="flex items-center justify-between gap-3">
                                    <Label className="text-sm font-semibold text-gray-900">
                                      Free Cancellation Up To{" "}
                                      <span className="font-normal text-gray-500">
                                        (No. of Hours)
                                      </span>
                                    </Label>

                                    <Input
                                      type="number"
                                      min="0"
                                      max="999"
                                      value={policy.freeCancellationHours}
                                      onChange={(event) =>
                                        updateCancellationPolicy(
                                          "freeCancellationHours",
                                          Math.min(999, Math.max(0, Number(event.target.value || 0)))
                                        )
                                      }
                                      className="h-9 w-20 sm:w-24 shrink-0 text-center bg-white"
                                      aria-label="Free cancellation hours"
                                    />
                                  </div>
                                  <div className="space-y-3">
                                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                      <div>
                                        <Label className="text-sm font-semibold text-gray-900">
                                          Cancellation Tiers (Days Before Check-in)
                                        </Label>
                                        <p className="text-xs text-gray-500">
                                          Highest days apply first. Example: 60+ days = 20%, 45-59 days = 50%.
                                        </p>
                                      </div>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addCancellationTier}
                                        className="w-fit"
                                      >
                                        Add Cancellation Tier
                                      </Button>
                                    </div>

                                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                                      {[...(policy.tiers || [])]
                                        .sort((a, b) => b.daysBeforeCheckIn - a.daysBeforeCheckIn)
                                        .map((tier) => (
                                          <div key={tier.id} className="rounded-lg border border-sky-100 bg-white p-3">
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
                                              <div className="space-y-1">
                                                <Label className="text-xs font-medium text-gray-700">
                                                  Days Before Check-in
                                                </Label>
                                                <Input
                                                  type="number"
                                                  min="0"
                                                  value={tier.daysBeforeCheckIn}
                                                  onChange={(event) =>
                                                    updateCancellationTier(
                                                      tier.id,
                                                      "daysBeforeCheckIn",
                                                      event.target.value,
                                                    )
                                                  }
                                                  className="h-9"
                                                />
                                              </div>
                                              <div className="space-y-1">
                                                <Label className="text-xs font-medium text-gray-700">
                                                  Penalty (%)
                                                </Label>
                                                <Input
                                                  type="number"
                                                  min="0"
                                                  max="100"
                                                  value={tier.penaltyPercentage}
                                                  onChange={(event) =>
                                                    updateCancellationTier(
                                                      tier.id,
                                                      "penaltyPercentage",
                                                      event.target.value,
                                                    )
                                                  }
                                                  className="h-9"
                                                />
                                              </div>
                                              <div className="space-y-1">
                                                <Label className="text-xs font-medium text-gray-700">
                                                  Based On <span className="text-red-500">*</span>
                                                </Label>
                                                <div className="p-1 mt-1 rounded-lg border border-gray-300 bg-white px-2">
                                                  <Select
                                                    value={tier.penaltyBasis}
                                                    onValueChange={(value) =>
                                                      updateCancellationTier(
                                                        tier.id,
                                                        "penaltyBasis",
                                                        value,
                                                      )
                                                    }
                                                  >
                                                    <SelectTriggerUI className="h-10 rounded-lg bg-white border-gray-200 text-sm">
                                                      <SelectValue placeholder="Select" />
                                                    </SelectTriggerUI>
                                                    <SelectContent>
                                                      <SelectItem value="2">Full Booking</SelectItem>
                                                      <SelectItem value="1">First Night</SelectItem>
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                              </div>

                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() =>
                                                  requestDeleteConfirmation({
                                                    type: "cancellation-tier",
                                                    id: tier.id,
                                                  })
                                                }
                                                className="h-9 w-9 text-red-600 hover:bg-red-50"
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                    {errors.cancellationPolicy && (
                                      <p className="text-xs text-red-600">
                                        {errors.cancellationPolicy}
                                      </p>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </CardContent>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </Card>
                </TabsContent>

              )}

              {formData.forMiqaat && miqaatAccommodationMode === "property-type" && (
                <TabsContent value="property-type" className="mt-5 space-y-4">
                  <div className="border-t-2 border-[#5B9BD5]/20 pt-3"></div>
                  <div className="pt-0">
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem
                        value="property-type-config"
                        className="border-0"
                      >
                        <AccordionTrigger
                          value="property-type-config"
                          className="px-0 py-3 hover:no-underline w-full [&>svg]:mt-1"
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <Building2 className="w-5 h-5 text-[#5B9BD5]" />
                            <div className="text-left">
                              <Label
                                className="text-base font-semibold text-gray-900 cursor-pointer"
                                style={{ color: "#5B9BD5" }}
                              >
                                Property Type Configuration
                              </Label>
                              <p className="text-xs text-gray-600 font-normal">
                                Configure charges for property types by age group
                              </p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-0 pb-0 pt-5">
                          <Card className="overflow-hidden rounded-xl border border-[#5B9BD5]/20 shadow-sm">
                            <CardHeader className="bg-blue-50/70 px-5 py-4">
                              <CardTitle
                                className="text-base font-semibold text-[#5B9BD5]"
                              >
                                Property Type Charges
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 sm:p-6">
                              <div className="hidden sm:block">
                                <table className="w-full">
                                  <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50">
                                      <th className="p-3 text-left text-sm font-semibold text-gray-900">
                                        Property Type
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Infant Amount
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Child Amount
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Adult Amount
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Senior Amount
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Extras
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {propertyTypeOption.map((propType, index) => {
                                      const propCharges =
                                        formData.propertyTypeCharges.find(
                                          (c) =>
                                            c.propertyTypeId ===
                                            String(propType.id),
                                        );
                                      return (
                                        <tr
                                          key={propType.id}
                                          className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                                        >
                                          <td className="p-3 text-sm font-semibold text-gray-900">
                                            {propType.name}
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              inputMode="decimal"
                                              step="any"
                                              placeholder="0.00"
                                              value={propCharges?.infantCharge || ""}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          // infantCharge: parseFloat(value) || 0,
                                                          infantCharge: value === "" ? 0 : Number(value),
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                propCharges?.childCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          childCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                propCharges?.adultCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          adultCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                propCharges?.seniorCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          seniorCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                propCharges?.extraCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          extraCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );

                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              <div className="sm:hidden p-4 space-y-4">
                                {propertyTypeOption.map((propType) => {
                                  const propCharges =
                                    formData.propertyTypeCharges.find(
                                      (c) =>
                                        c.propertyTypeId === String(propType.id),
                                    );
                                  return (
                                    <div
                                      key={propType.id}
                                      className="border rounded-lg bg-white"
                                    >
                                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b px-4 py-3">
                                        <h3 className="font-semibold text-gray-900 text-sm">
                                          {propType.name}
                                        </h3>
                                      </div>
                                      <div className="p-4">
                                        <div className="grid grid-cols-2 gap-3">
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Infant Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                propCharges?.infantCharge || 0
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          infantCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Child Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                propCharges?.childCharge || 0
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          childCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Adult Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                propCharges?.adultCharge || 0
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          adultCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Senior Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                propCharges?.seniorCharge || 0
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          seniorCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Extras
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                propCharges?.extraCharge || 0
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.propertyTypeCharges.map(
                                                    (c) =>
                                                      c.propertyTypeId ===
                                                        String(propType.id)
                                                        ? {
                                                          ...c,
                                                          extraCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );

                                                setFormData({
                                                  ...formData,
                                                  propertyTypeCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </CardContent>
                          </Card>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </TabsContent>
              )}

              <TabsContent value="services" className="mt-5 space-y-4">
                {/* ATRAAF CONFIGRATION START */}
                <div className="border-t-2 border-[#5B9BD5]/20 pt-3">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="atraaf-config" className="border-0">
                      <AccordionTrigger
                        value="atraaf-config"
                        className="px-0 py-3 hover:no-underline w-full [&>svg]:mt-1"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <MapPin className="w-5 h-5 text-[#5B9BD5]" />
                          <div className="text-left">
                            <Label
                              className="text-base font-semibold text-gray-900 cursor-pointer"
                              style={{ color: "#5B9BD5" }}
                            >
                              Atraaf Configuration
                            </Label>
                            <p className="text-xs text-gray-600 font-normal">
                              Configure charges for atraafs (only those with
                              lawazim required) by age group
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      {/* <AccordionContent className="px-0 pb-0 pt-5"> */}
                      <AccordionContent className="">
                        <Card className="overflow-hidden rounded-xl border border-[#5B9BD5]/20 shadow-sm">
                          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-2 pt-2 rounded-t-xl">
                            <CardTitle
                              className="text-base font-semibold text-[#5B9BD5]"
                            >
                              Atraaf Charges
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-4 sm:p-6">
                            <div className="hidden sm:block">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b border-gray-200 bg-gray-50">
                                    <th className="p-3 text-left text-sm font-semibold text-gray-900">
                                      Atraaf Name
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Infant Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Child Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Adult Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Senior Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Extras
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {atraafTypeOption
                                    ?.filter((atraaf) => atraaf.lawazimRequired)
                                    .map((atraaf, index) => {
                                      const atraafCharges =
                                        formData.atraafCharges.find(
                                          (c) => c.atraafId === atraaf.atraafId,
                                        );
                                      return (
                                        <tr
                                          key={atraaf.atraafId}
                                          className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                                        >
                                          <td className="p-3 text-sm font-semibold text-gray-900">
                                            {atraaf.atraafName}
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.infantCharge ||
                                                ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId ===
                                                        atraaf.atraafId
                                                        ? {
                                                          ...c,
                                                          infantCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.childCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId ===
                                                        atraaf.atraafId
                                                        ? {
                                                          ...c,
                                                          childCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.adultCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId ===
                                                        atraaf.atraafId
                                                        ? {
                                                          ...c,
                                                          adultCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.seniorCharge ||
                                                ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId ===
                                                        atraaf.atraafId
                                                        ? {
                                                          ...c,
                                                          seniorCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.extraCharge || 0
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId ===
                                                        atraaf.atraafId
                                                        ? {
                                                          ...c,
                                                          extraCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );

                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                            <div className="sm:hidden p-4 space-y-4">
                              {atraafTypeOption
                                ?.filter((atraaf) => atraaf.lawazimRequired)
                                .map((atraaf) => {
                                  const atraafCharges =
                                    formData.atraafCharges.find(
                                      (c) => c.atraafId === atraaf.id,
                                    );
                                  return (
                                    <div
                                      key={atraaf.atraafId}
                                      className="border rounded-lg bg-white"
                                    >
                                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b px-4 py-3">
                                        <h3 className="font-semibold text-gray-900 text-sm">
                                          {atraaf.atraafName}
                                        </h3>
                                      </div>
                                      <div className="p-4">
                                        <div className="grid grid-cols-2 gap-3">
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Infant Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.infantCharge ||
                                                ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId === atraaf.id
                                                        ? {
                                                          ...c,
                                                          infantCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Child Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.childCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId === atraaf.id
                                                        ? {
                                                          ...c,
                                                          childCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Adult Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.adultCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId === atraaf.id
                                                        ? {
                                                          ...c,
                                                          adultCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Senior Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.seniorCharge ||
                                                ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId === atraaf.id
                                                        ? {
                                                          ...c,
                                                          seniorCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Extras
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                atraafCharges?.extraCharge || 0
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.atraafCharges.map(
                                                    (c) =>
                                                      c.atraafId ===
                                                        atraaf.atraafId
                                                        ? {
                                                          ...c,
                                                          extraCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );

                                                setFormData({
                                                  ...formData,
                                                  atraafCharges: updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </CardContent>
                        </Card>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
                {/* ATRAAF CONFIGRATION END */}

                {/* PORT ACCORDION START */}
                <div className="border-t-2 border-[#5B9BD5]/20 pt-3">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem
                      value="property-type-config"
                      className="border-0"
                    >
                      <AccordionTrigger
                        value="property-type-config"
                        className="px-0 py-3 hover:no-underline w-full [&>svg]:mt-1"
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <Plane className="w-5 h-5 text-[#5B9BD5]" />
                          <div className="text-left">
                            <Label
                              className="text-base font-semibold text-gray-900 cursor-pointer"
                              style={{ color: "#5B9BD5" }}
                            >
                              Transport Configuration
                            </Label>
                            <p className="text-xs text-gray-600 font-normal">
                              Configure arrival and departure charges by port
                              and age group
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="">
                        <Card className="border-[#5B9BD5]/30 mb-4">
                          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 pb-2 pt-2 rounded-t-xl">
                            <CardTitle
                              className="text-base font-semibold text-[#5B9BD5]"
                            >
                              Arrival Charges
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-4 sm:p-6">
                            <div className="hidden sm:block">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b border-gray-200 bg-gray-50">
                                    <th className="p-3 text-left text-sm font-semibold text-gray-900">
                                      Port Name
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Infant Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Child Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Adult Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Senior Amount
                                    </th>
                                    <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                      Extras
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {portOption?.map((port, index) => {
                                    const portCharges =
                                      formData.arrivalCharges.find(
                                        (c) =>
                                          String(c.portId) ===
                                          String(port.portId),
                                      );
                                    return (
                                      <tr
                                        key={port.portId}
                                        className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                                      >
                                        <td className="p-3 text-sm font-semibold text-gray-900">
                                          {port.portName}{" "}
                                          <span className="text-gray-500">
                                            ({port.portShortName})
                                          </span>
                                        </td>
                                        <td className="p-3">
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.infantCharge || ""
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        infantCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );
                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </td>
                                        <td className="p-3">
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.childCharge || ""
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        childCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );
                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </td>
                                        <td className="p-3">
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.adultCharge || ""
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        adultCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );
                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </td>
                                        <td className="p-3">
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.seniorCharge || ""
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        seniorCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );
                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </td>
                                        <td className="p-3">
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.extraCharge || ""
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        extraCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );

                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            <div className="sm:hidden p-4 space-y-4">
                              {portOption?.map((port, index) => {
                                const portCharges =
                                  formData.arrivalCharges.find(
                                    (c) =>
                                      String(c.portId) === String(port.portId),
                                  );
                                return (
                                  <div
                                    key={port.portId}
                                    className="border rounded-lg bg-white"
                                  >
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b px-4 py-3">
                                      <h3 className="font-semibold text-gray-900 text-sm">
                                        {port.portName}{" "}
                                        <span className="text-gray-600 text-xs">
                                          ({port.portShortName})
                                        </span>
                                      </h3>
                                    </div>
                                    <div className="p-4">
                                      <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1">
                                          <Label className="text-xs font-medium text-gray-900">
                                            Infant Amount
                                          </Label>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.infantCharge || ""
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        infantCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );
                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs font-medium text-gray-900">
                                            Child Amount
                                          </Label>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.childCharge || ""
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        childCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );
                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs font-medium text-gray-900">
                                            Adult Amount
                                          </Label>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.adultCharge || ""
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        adultCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );
                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs font-medium text-gray-900">
                                            Senior Amount
                                          </Label>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.seniorCharge || ""
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        seniorCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );
                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs font-medium text-gray-900">
                                            Extras
                                          </Label>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            value={
                                              portCharges?.extraCharge || 0
                                            }
                                            onChange={(e) => {
                                              const updatedCharges =
                                                formData.arrivalCharges.map(
                                                  (c) =>
                                                    String(c.portId) ===
                                                      String(port.portId)
                                                      ? {
                                                        ...c,
                                                        extraCharge:
                                                          parseFloat(
                                                            e.target.value,
                                                          ) || 0,
                                                      }
                                                      : c,
                                                );

                                              setFormData({
                                                ...formData,
                                                arrivalCharges: updatedCharges,
                                              });
                                            }}
                                            className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </Card>

                        <div className="border-t-2 border-[#5B9BD5]/20 pt-3 space-y-3">
                          <Card className="overflow-hidden rounded-xl border border-[#5B9BD5]/20 shadow-sm">
                            <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 pb-2 pt-2 rounded-t-xl">
                              <CardTitle
                                className="text-sm font-semibold"
                                style={{ color: "#5B9BD5" }}
                              >
                                Departure Charges
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 sm:p-6">
                              <div className="hidden sm:block">
                                <table className="w-full">
                                  <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50">
                                      <th className="p-3 text-left text-sm font-semibold text-gray-900">
                                        Port Name
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Infant Amount
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Child Amount
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Adult Amount
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Senior Amount
                                      </th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">
                                        Extras
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {portOption?.map((port, index) => {
                                      const portCharges =
                                        formData.departureCharges.find(
                                          (c) =>
                                            String(c.portId) ===
                                            String(port.portId),
                                        );
                                      return (
                                        <tr
                                          key={port.portId}
                                          className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                                        >
                                          <td className="p-3 text-sm font-semibold text-gray-900">
                                            {port.portName}{" "}
                                            <span className="text-gray-500">
                                              ({port.portShortName})
                                            </span>
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.infantCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          infantCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.childCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          childCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.adultCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          adultCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.seniorCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          seniorCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                          <td className="p-3">
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.extraCharge || 0
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          extraCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );

                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              <div className="sm:hidden p-4 space-y-4">
                                {portOption.map((port, index) => {
                                  const portCharges =
                                    formData.departureCharges.find(
                                      (c) =>
                                        String(c.portId) ===
                                        String(port.portId),
                                    );
                                  return (
                                    <div
                                      key={port.portId}
                                      className="border rounded-lg bg-white"
                                    >
                                      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-b px-4 py-3">
                                        <h3 className="font-semibold text-gray-900 text-sm">
                                          {port.portName}{" "}
                                          <span className="text-gray-600 text-xs">
                                            ({port.portShortName})
                                          </span>
                                        </h3>
                                      </div>
                                      <div className="p-4">
                                        <div className="grid grid-cols-2 gap-3">
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Infant Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.infantCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          infantCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Child Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.childCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          childCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Adult Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.adultCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          adultCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Senior Amount
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.seniorCharge || ""
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          seniorCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );
                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs font-medium text-gray-900">
                                              Extras
                                            </Label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              placeholder="0.00"
                                              value={
                                                portCharges?.extraCharge || 0
                                              }
                                              onChange={(e) => {
                                                const updatedCharges =
                                                  formData.departureCharges.map(
                                                    (c) =>
                                                      String(c.portId) ===
                                                        String(port.portId)
                                                        ? {
                                                          ...c,
                                                          extraCharge:
                                                            parseFloat(
                                                              e.target.value,
                                                            ) || 0,
                                                        }
                                                        : c,
                                                  );

                                                setFormData({
                                                  ...formData,
                                                  departureCharges:
                                                    updatedCharges,
                                                });
                                              }}
                                              className="h-10 rounded-lg bg-white border-gray-200 text-center text-sm"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </CardContent>
                          </Card>

                          <div className="border-t-2 border-[#5B9BD5]/20 pt-3">
                            <Card className="border-[#5B9BD5]/30 mb-4">
                              <CardHeader className="bg-gradient-to-r from-sky-50 to-blue-50 pb-2 pt-2 rounded-t-xl">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                  <CardTitle className="text-base font-semibold text-[#5B9BD5]">
                                    Inter Venue Charges
                                  </CardTitle>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-8 border-[#5B9BD5]/40 text-[#5B9BD5] hover:bg-sky-50"
                                    onClick={addInterVenueCharge}
                                  >
                                    <Plus className="mr-1 h-4 w-4" />
                                    Add Row
                                  </Button>
                                </div>
                              </CardHeader>
                              <CardContent className="p-4 sm:p-6">
                                {interVenueCharges.length === 0 ? (
                                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5 text-center text-sm text-gray-500">
                                    No inter venue charges added yet.
                                  </div>
                                ) : (
                                  <div className="space-y-3">
                                    {interVenueCharges.map((row, index) => (
                                      <div
                                        key={row.id}
                                        className="grid gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm xl:grid-cols-[minmax(160px,1fr)_minmax(160px,1fr)_repeat(5,minmax(120px,0.8fr))_40px]"
                                      >
                                        <div className="space-y-1">
                                          <Label className="text-xs font-medium text-gray-900">
                                            From Venue
                                          </Label>

                                          <div className="p-2 mt-1 rounded-lg border border-gray-300 bg-white px-2">
                                            <Select
                                              value={row.fromVenueId}
                                              onValueChange={(value) =>
                                                updateInterVenueCharge(
                                                  row.id,
                                                  "fromVenueId",
                                                  value,
                                                )
                                              }
                                            >
                                              <SelectTriggerUI className="h-10 border-0 shadow-none focus:ring-0">
                                                <SelectValue placeholder="Select from venue" />
                                              </SelectTriggerUI>

                                              <SelectContent>
                                                {venueOptions.map((venue) => (
                                                  <SelectItem
                                                    key={venue.venueId}
                                                    value={String(venue.publicId)}
                                                  >
                                                    {venue.venueName}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </div>


                                        <div className="space-y-1">
                                          <Label className="text-xs font-medium text-gray-900">
                                            To Venue
                                          </Label>
                                          <div className="p-2 mt-1 rounded-lg border border-gray-300 bg-white px-2">
                                            <Select
                                              value={row.toVenueId}
                                              onValueChange={(value) =>
                                                updateInterVenueCharge(
                                                  row.id,
                                                  "toVenueId",
                                                  value,
                                                )
                                              }
                                            >
                                              <SelectTriggerUI className="h-10 rounded-lg bg-white border-gray-200 text-sm">
                                                <SelectValue placeholder="Select to venue" />
                                              </SelectTriggerUI>
                                              <SelectContent>
                                                {venueOptions.map((venue) => (
                                                  <SelectItem
                                                    key={venue.venueId}
                                                    value={String(venue.publicId)}
                                                  >
                                                    {venue.venueName}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </div>

                                        {([
                                          ["infantCharge", "Infant Amount"],
                                          ["childCharge", "Child Amount"],
                                          ["adultCharge", "Adult Amount"],
                                          ["seniorCharge", "Senior Amount"],
                                          ["extraCharge", "Extras"],
                                        ] as const).map(([field, label]) => (
                                          <div key={field} className="space-y-1">
                                            <Label className="mt-1 text-xs font-medium text-gray-900">
                                              {label}
                                            </Label>
                                            <Input
                                              type="number"
                                              min="0"
                                              step="0.01"
                                              value={row[field] || ""}
                                              onChange={(event) =>
                                                updateInterVenueCharge(
                                                  row.id,
                                                  field,
                                                  event.target.value,
                                                )
                                              }
                                              placeholder="0.00"
                                              className="h-10 rounded-lg bg-white border-gray-200 text-sm"
                                            />
                                          </div>
                                        ))}
                                        <div className="flex items-end">
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-10 w-10 text-red-500 hover:bg-red-50 hover:text-red-700"
                                            onClick={() =>
                                              requestDeleteConfirmation({
                                                type: "inter-venue-charge",
                                                id: row.id,
                                              })
                                            }
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {errors.interVenueCharges && (
                                  <p className="mt-2 text-sm text-red-600">
                                    {errors.interVenueCharges}
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
                {/* ACCORDION END */}

                <div className="border-t-2 border-[#5B9BD5]/20 pt-3">
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="additional-charges" className="border-0">
                      <AccordionTrigger className="w-full px-0 py-3 hover:no-underline [&>svg]:mt-1">
                        <div className="flex flex-1 items-center gap-2">
                          <Settings2 className="h-5 w-5 text-[#5B9BD5]" />
                          <div className="text-left">
                            <Label className="cursor-pointer text-base font-semibold text-[#5B9BD5]">
                              Additional Charges
                            </Label>
                            <p className="text-xs font-normal text-gray-600">
                              Configure charges for active services marked as chargeable in Tour Service Master
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      {/* <AccordionContent className="px-0 pb-0 pt-3"> */}
                      <AccordionContent className="">
                        <Card className="overflow-hidden rounded-xl border border-[#5B9BD5]/20 shadow-sm">
                          <CardContent className="p-4">
                            {formData.additionalServiceCharges.length === 0 ? (
                              <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-sm text-gray-500">
                                No active chargeable services found in Tour Service Master
                              </div>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full min-w-[900px]">
                                  <thead>
                                    <tr className="border-b border-gray-200 bg-gray-50">
                                      <th className="p-3 text-left text-sm font-semibold text-gray-900">Service Name</th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">Infant Amount</th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">Child Amount</th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">Adult Amount</th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">Senior Amount</th>
                                      <th className="p-3 text-center text-sm font-semibold text-gray-900">Extras</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {formData.additionalServiceCharges.map((service, index) => (
                                      <tr key={service.tourServiceId} className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                                        <td className="p-3 text-sm font-semibold text-gray-900">{service.serviceName}</td>
                                        {([
                                          ["infantCharge", "Infant"],
                                          ["childCharge", "Child"],
                                          ["adultCharge", "Adult"],
                                          ["seniorCharge", "Senior"],
                                          ["extraCharge", "Extra"],
                                        ] as const).map(([field, label]) => (
                                          <td key={field} className="p-3">
                                            <Input
                                              type="number"
                                              min="0"
                                              step="0.01"
                                              value={service[field] || ""}
                                              onChange={(event) => {
                                                const value = Math.max(0, Number(event.target.value || 0));
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  additionalServiceCharges: prev.additionalServiceCharges.map((item) =>
                                                    item.tourServiceId === service.tourServiceId ? { ...item, [field]: value } : item,
                                                  ),
                                                }));
                                              }}
                                              className="mx-auto h-10 max-w-[160px] text-center"
                                              placeholder="0.00"
                                              aria-label={`${service.serviceName} ${label} charge`}
                                            />
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </TabsContent>
            </Tabs>
          </div>
          {/* Transport Configuration */}
          {/* Save Button */}
          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-3">
            <Button
              onClick={() => {
                resetForm();
                navigate(-1);
              }}
              variant="outline"
              className="border-gray-300 w-full sm:w-auto h-9"
            >
              Cancel
            </Button>
            {canAdd && (
              <Button
                onClick={handleSave}
                className="bg-[#5B9BD5] hover:bg-[#4A8BC2] text-white w-full sm:w-auto h-9"
              >
                <Save className="w-4 h-4 mr-2" />
                {"Save Lawazim"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={!!deleteConfirmTarget}
        onOpenChange={(open) => {
          if (!open) cancelDeleteConfirmation();
        }}
        title={
          deleteConfirmTarget?.type === "cancellation-tier"
            ? "Delete Cancellation Tier"
            : "Delete Inter Venue Charge"
        }
        description={
          deleteConfirmTarget?.type === "cancellation-tier"
            ? "Are you sure you want to delete this cancellation tier?"
            : "Are you sure you want to delete this inter venue charge?"
        }
        confirmText="Yes, Delete"
        onConfirm={confirmDelete}
        onCancel={cancelDeleteConfirmation}
      />
    </div>
  );
}
